import { rgba } from 'polished';
import Delta from 'quill-delta';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronDownIcon } from '@treams/treams-icons';
import {
	AiGeneratedCompetenciesSuggestion,
	CompetencyLevelSet,
	JobTitle,
	useAdvanceAiGeneratedCompetenciesChatMutation,
	useAttachJobCompetencyMutation,
	useCompetencyLevelSetsQuery,
	useCreateCompetencyMutation,
	useStartAiGeneratedCompetenciesChatMutation,
} from '@treams/treams-network';
import {
	Button,
	Checkbox,
	Heading3,
	IconButton,
	Paragraph,
	Popover,
	Select,
	Tabs,
	tokens,
} from '@treams/treams-ui';

import { SuggestionsAIChatModal } from '../../../goals/goal-ai/SuggestionsAIChatModal';

const CompetencySuggestionContainer = styled(Box)`
	background: ${tokens.surface.white};
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.container}px;
`;

const CollapseButton = styled(Box)<{ collapsed: boolean }>`
	transition: transform 0.2s;
	transform: rotate(${(props) => (props.collapsed ? -90 : 0)}deg);
`;

const Collapsable = styled(Box)<{ collapsed: boolean }>`
	transition:
		max-height 0.2s,
		padding-top 0.2s;
	max-height: ${(props) => (props.collapsed ? 0 : 300)}px;
	padding-top: ${(props) => (props.collapsed ? 0 : 8)}px;
	box-sizing: border-box;
	overflow: hidden;
`;

const LevelCriteria = styled(Paragraph)`
	background: ${() => rgba(tokens.border.subtle, 0.3)};
	border-radius: 4px;
	padding: 8px 12px;
`;

function CompetencySuggestion(props: {
	data: AiGeneratedCompetenciesSuggestion;
	levelSet: CompetencyLevelSet;
	selected: boolean;
	onToggleSelected: () => void;
}) {
	const { localize } = useLocalize();
	const [collapsed, setCollapsed] = useState(true);
	const [selectedLevel, setSelectedLevel] = useState(0);

	return (
		<CompetencySuggestionContainer mb={2}>
			<Flex alignItems="center">
				<CollapseButton collapsed={collapsed} ml={-2} mt={-1} mr={1} flexShrink={0}>
					<IconButton
						icon={<ChevronDownIcon />}
						type="plain"
						onClick={() => setCollapsed(!collapsed)}
					/>
				</CollapseButton>
				<Heading3 mb={1} flexGrow={1}>
					{props.data.title}
				</Heading3>
				<Checkbox ml={3} checked={props.selected} onClick={props.onToggleSelected} flexShrink={0} />
			</Flex>
			<Collapsable collapsed={collapsed}>
				<Paragraph>{props.data.description}</Paragraph>
				<Tabs mt={3}>
					{props.data.levels.map((criteria, idx) => (
						<Tabs.Item
							key={idx}
							title={localize(props.levelSet.levels[idx].title).text}
							selected={selectedLevel === idx}
							onClick={() => setSelectedLevel(idx)}
						/>
					))}
				</Tabs>
				<LevelCriteria mt={2}>{props.data.levels[selectedLevel]}</LevelCriteria>
			</Collapsable>
		</CompetencySuggestionContainer>
	);
}

const CompetencySuggestionsContainer = styled(Box)`
	max-width: 80%;
`;

function CompetencySuggestions(props: {
	data: AiGeneratedCompetenciesSuggestion[];
	levelSet: CompetencyLevelSet;
	onTake: (suggestion: AiGeneratedCompetenciesSuggestion[]) => void;
}) {
	const { t } = useTranslation();
	const [deselected, setDeselected] = useState<number[]>([]);

	return (
		<>
			<CompetencySuggestionsContainer mb={2}>
				{props.data.map((suggestion, idx) => (
					<CompetencySuggestion
						key={idx}
						data={suggestion}
						levelSet={props.levelSet}
						selected={!deselected.includes(idx)}
						onToggleSelected={() => {
							if (deselected.includes(idx)) {
								setDeselected(deselected.filter((i) => i !== idx));
							} else {
								setDeselected([...deselected, idx]);
							}
						}}
					/>
				))}
			</CompetencySuggestionsContainer>
			<Flex>
				<Button
					onClick={async () =>
						await props.onTake(props.data.filter((_, idx) => !deselected.includes(idx)))
					}
					title={t('competencies.ai.take')}
				/>
			</Flex>
		</>
	);
}

const fadeOut = keyframes`
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
	}
`;

const LevelSetSelectorContainer = styled(Flex)<{ picked: boolean }>`
	flex-direction: column;
	align-items: center;
	gap: 10px;

	animation-delay: 400ms;
	animation-duration: 200ms;
	animation-timing-function: linear;
	animation-fill-mode: both;
	${(props) =>
		props.picked &&
		css`
			animation-name: ${fadeOut};
		`}
`;

export default function JobCompetencyGeneratorModal(props: {
	jobTitle: JobTitle;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();
	const [start, { loading: starting }] = useStartAiGeneratedCompetenciesChatMutation();
	const [advance, { loading: advancing }] = useAdvanceAiGeneratedCompetenciesChatMutation();

	const { data } = useCompetencyLevelSetsQuery({
		variables: { isArchived: false, first: 30 },
	});
	const levelSets = (data?.competencyLevelSets.edges ?? []).map(
		({ node }) => node as CompetencyLevelSet,
	);
	const [levelSet, setLevelSet] = useState<CompetencyLevelSet | null>(null);
	const [isSelectingLevelSet, setIsSelectingLevelSet] = useState(true);

	const [create] = useCreateCompetencyMutation();
	const [attach] = useAttachJobCompetencyMutation();

	const onTakeSuggestion = async (suggestions) => {
		for (const suggestion of suggestions) {
			const input = {
				competencyLevelSetID: levelSet!.id,
				title: [
					{
						languageID: language.id,
						text: suggestion.title,
					},
				],
				description: [
					{
						languageID: language.id,
						text: {
							ops: [
								{
									insert: suggestion.description,
								},
							],
						} as Delta,
					},
				],
				levels: suggestion.levels.map((criteria, idx) => ({
					competencyLevelID: levelSet!.levels[idx].id,
					enabled: true,
					criteria: [
						{
							languageID: language.id,
							text: {
								ops: [
									{
										insert: criteria,
									},
								],
							} as Delta,
						},
					],
				})),
				jobTitleIDs: [props.jobTitle.id],
				attachableToGoal: true,
			};

			const result = await create({
				variables: {
					input,
				},
			});

			// TODO: Picks 0 level by default, but perhaps we should incorporate selecting levels in the flow?
			await attach({
				variables: {
					input: {
						competencyID: result.data!.createCompetency.competency.id,
						competencyLevel: 0,
						jobTitleID: props.jobTitle.id,
					},
				},
			});
		}

		props.onDismiss();
	};

	return (
		<Popover center backdrop visible>
			<SuggestionsAIChatModal<AiGeneratedCompetenciesSuggestion[]>
				translationKey="competencies"
				onDismiss={props.onDismiss}
				startChat={async (reset) => {
					const response = await start({
						variables: {
							input: {
								locale: language.id,
								reset,
								jobTitleID: props.jobTitle.id,
								levelSetID: levelSet!.id,
							},
						},
					});

					return { chat: response.data!.startAIGeneratedCompetenciesChat.chat };
				}}
				starting={starting}
				advanceChat={async (chatID, userResponse) => {
					const response = await advance({
						variables: {
							input: {
								locale: language.id,
								chatID,
								userResponse,
							},
						},
					});

					return {
						chat: response.data!.advanceAIGeneratedCompetenciesChat.chat,
						suggestion: response.data!.advanceAIGeneratedCompetenciesChat.suggestion ?? null,
					};
				}}
				advancing={advancing}
				renderSuggestion={(suggestions) => (
					<CompetencySuggestions
						data={suggestions}
						levelSet={levelSet!}
						onTake={onTakeSuggestion}
					/>
				)}
				isPreConversation={isSelectingLevelSet}
				preConversationMessages={[t('competencies.ai.levelSet.prompt')]}
				preConversationAccessory={
					<LevelSetSelectorContainer my={3} picked={!!levelSet}>
						<Select
							options={levelSets.map((set) => ({
								value: set.id,
								title: localize(set.title).text,
							}))}
							selectedValue={levelSet?.id}
							onChange={(value) => {
								setLevelSet(levelSets.find((set) => set.id === value)!);
								setTimeout(() => setIsSelectingLevelSet(false), 600);
							}}
							placeholder={t('common.settings.competencies.create.selectExisting')}
							search={false}
						/>
						<Paragraph>
							{t('common.settings.competencies.create.or')}&nbsp;
							<Link
								to="/settings/competencies/create/levels"
								style={{ textDecoration: 'underline' }}
							>
								{t('common.settings.competencies.create.createLevelSet').toLowerCase()}
							</Link>
						</Paragraph>
					</LevelSetSelectorContainer>
				}
				preConversationResult={levelSet && localize(levelSet.title).text}
				onReset={() => {
					setLevelSet(null);
					setIsSelectingLevelSet(true);
				}}
			/>
		</Popover>
	);
}
